// Stripe Transactions page — the words, and the small list rules, kept out of the
// component so they can be tested. Wording is the owner's, verbatim
// (plans/plan_stripe_link_worklist_2026_10_26.md: D3, D6, D19, D21, D22, D23).
import { priceStrFormatter } from "./formatters/priceStrFormatter";

const toInt = (v) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : 0;
};

/** Cents strings compared as integers (" 31986" and "31986" match). */
export const amountsDiffer = (a, b) => toInt(a) !== toInt(b);

/** The line under a card saying why the payment is on the list. */
export function reasonLine(row) {
  if (!row) return null;
  switch (row.reason) {
    case "mismatch":
      return `Charged amount didn't match invoice #${row.invoiceNum} (${priceStrFormatter(row.invoiceTotal)}). Fix the invoice, then link.`;
    case "ready":
      return `Invoice #${row.invoiceNum} now matches - link it.`;
    case "no_invoice":
      return "No invoice attached.";
    case "recording":
      return "The phone hasn't finished recording this payment.";
    case "paid_elsewhere":
      return `Invoice #${row.invoiceNum} is already paid.`;
    case "invoice_missing":
      return "Its invoice no longer exists - link it to the right invoice.";
    default:
      return null;
  }
}

// Same test the server (and the cash paths) use for "the invoice already has its tip".
const TIP_LINE = /^\s*tip\s*$/i;
export const invoiceHasTipLine = (invoice) =>
  (invoice?.invoiceItems || []).some((item) => TIP_LINE.test(item?.itemName || ""));

/**
 * What Confirm books on this invoice (the server's rule): the invoice total, plus
 * the tip unless the invoice already has a Tip line (D22).
 */
export function bookedTotal(txn, invoice) {
  const tip = toInt(txn?.tip);
  return toInt(invoice?.invoiceTotal) + (tip > 0 && !invoiceHasTipLine(invoice) ? tip : 0);
}

/**
 * Does Confirm book something other than what the card was charged (amount +
 * tip)? (D23.) For an invoice without a Tip line this is exactly "amount !=
 * invoice total"; with a Tip line it is "amount + tip != invoice total" — an
 * invoice the office fixed to include the tip is a match, not a false alarm.
 */
export const bookingDiffers = (txn, invoice) =>
  bookedTotal(txn, invoice) !== toInt(txn?.amount) + Math.max(toInt(txn?.tip), 0);

/**
 * Dialog warning when Confirm would book something other than what was charged
 * (owner D21). It says what Confirm will do — the receipt email links to the
 * invoice, so the customer sees exactly this total.
 */
export function mismatchWarning(txn, invoice) {
  if (!txn || !invoice || invoice.invoicePaid) return null;
  if (!bookingDiffers(txn, invoice)) return null;
  const tip = toInt(txn.tip);
  const paidAt = bookedTotal(txn, invoice);
  const charged =
    tip > 0
      ? `${priceStrFormatter(txn.amount)} + ${priceStrFormatter(String(tip))} tip`
      : priceStrFormatter(txn.amount);
  return (
    `This payment (${charged}) doesn't match invoice #${invoice.invoiceNum} (${priceStrFormatter(invoice.invoiceTotal)}). ` +
    `Confirming marks it paid at ${priceStrFormatter(String(paidAt))} and emails the customer that receipt. ` +
    `Fix the invoice first unless this payment covers it.`
  );
}

/** Dialog line when the office types a different invoice than the payment was taken for. */
export function wrongInvoiceLine(txn, invoice) {
  if (!txn?.invoiceNum || !invoice?.invoiceNum) return null;
  if (String(txn.invoiceNum) === String(invoice.invoiceNum)) return null;
  return `This payment was taken for invoice #${txn.invoiceNum}.`;
}

const RECEIPT_SUFFIX = {
  sent: (r) => ` Receipt sent to ${r.sentEmail}.`,
  no_email: () => " No email on file - no receipt sent.",
  no_customer: () => " Customer record not found - no receipt sent.",
  already_sent: () => " This payment's receipt already went out - not sent again.",
  in_progress: () => " A receipt for this payment is already being sent.",
  send_failed: () => " Receipt email failed - see the Send Payment Receipt task.",
};

/** The snackbar after a successful link. `receiptStatus` null = an older server. */
export function linkResultMessage(result) {
  const head = `Invoice #${result.invoiceNum} (${result.customerName}) marked paid.`;
  const suffix = Object.prototype.hasOwnProperty.call(RECEIPT_SUFFIX, result.receiptStatus)
    ? RECEIPT_SUFFIX[result.receiptStatus]
    : null;
  if (suffix) return head + suffix(result);
  return head + (result.sentEmail ? ` Receipt sent to ${result.sentEmail}.` : " No email on file — no receipt sent.");
}

/**
 * Apollo 3 lazy queries RESOLVE `{ data: undefined, error }` on failure — they
 * never reject (react/hooks/useQuery.js) — so a try/catch alone never sees it.
 */
export function listOutcome(result) {
  if (!result || result.error || !Array.isArray(result.data?.getStripeTransactions)) {
    return { failed: true, rows: null };
  }
  return { failed: false, rows: [...result.data.getStripeTransactions] };
}

export function lookupOutcome(result) {
  if (!result || result.error) return { failed: true, invoice: null };
  return { failed: false, invoice: result.data?.getClientInvoiceByNum ?? null };
}

/**
 * Latest-request-wins: a refetch started after a link must not be overwritten by
 * an older response that lands later.
 */
export function createLatestGate() {
  let current = 0;
  return {
    begin: () => ++current,
    isCurrent: (token) => token === current,
  };
}

export const withoutRow = (rows, id) => (rows || []).filter((r) => r.id !== id);
